import { CalendarDays, CheckCircle2, Sparkles } from "lucide-react";
import { useMetaTags } from "../components/MetaTags";

type ChangelogDay = {
  date: string;
  headline: string;
  entries: string[];
};

const changelogDays: ChangelogDay[] = [
  {
    date: "September 18, 2026",
    headline: "Concept lines: see how your blocks connect",
    entries: [
      "Added Concepts, a canvas toggle that highlights ideas shared by text blocks and sticky notes and draws a colored line between them, one color per concept.",
      "Added optional matching by meaning with a 23 MB on-device model, so related words like “revenue” and “sales” connect without your text leaving the device.",
      "Repeated lines, such as a list item copied into another block, now link to the exact line they repeat.",
      "Put Draw directly in the mobile toolbar so drawing is one tap away.",
      "Downloaded on-device models (speech and concepts) now survive app updates instead of downloading again.",
      "Added Concepts settings: how many concepts to show, repeated-line linking, meaning matching and its sensitivity.",
      "Merged the two canvas toolbars into one bar with a zoom menu and a View menu; on narrow windows whole groups fold into More instead of scrolling out of sight.",
      "Checklists and wiki links now render in text blocks, so meeting action items show as checkboxes and [[links]] open the note.",
      "Meeting capture panels sit directly under their text, the graph opens framed below its controls, and quick reloads no longer trigger a second full load.",
    ],
  },
  {
    date: "July 9, 2026",
    headline: "Safer saving, recording recovery, and live collaboration",
    entries: [
      "Added live note rooms so collaborative editing can coordinate through a single shared authority.",
      "Improved save durability, update behavior, and title handling so open notes stay stable during active work.",
      "Changed recordings to preserve timed audio chunks and use the live transcript as the final transcript source.",
      "Added note IDs, richer version previews, safer restore flows, and better recovery scans for recordings and images.",
      "Improved AI workflows with a larger actions modal, progress feedback, whole-canvas chat context, and cleaner canvas placement.",
    ],
  },
  {
    date: "July 8, 2026",
    headline: "Recording workflow and canvas usability upgrades",
    entries: [
      "Made audio capture more reliable during active note taking and improved real-time transcription behavior.",
      "Fixed scrolling and focus issues around transcript panels, rich text blocks, and opened notes.",
      "Improved canvas navigation, wheel zoom responsiveness, compact controls, and mobile gesture handling.",
      "Added recovery hardening for app startup and documented crash recovery paths.",
      "Expanded product analytics and page-level conversion tracking on the public site.",
    ],
  },
  {
    date: "July 7, 2026",
    headline: "Responsive UI polish",
    entries: [
      "Cleaned up responsive layout problems across the app so core note workflows fit better on narrow screens.",
    ],
  },
  {
    date: "July 6, 2026",
    headline: "Hands-free recording improvements",
    entries: [
      "Added a draggable recorder experience with live transcription and meeting support tools.",
      "Improved recording cleanup with silence trimming and automatic stop behavior after extended quiet periods.",
    ],
  },
  {
    date: "July 3, 2026",
    headline: "Clearer recording, encryption, and AI meeting notes",
    entries: [
      "Added a consent reminder before recording starts.",
      "Clarified optional encryption behavior for notes and folders.",
      "Reduced over-broad product claims and strengthened AI meeting-notes content.",
      "Expanded public comparison and tool pages with more accurate positioning.",
    ],
  },
  {
    date: "July 2, 2026",
    headline: "Cloud-backed audio storage",
    entries: [
      "Moved plain-note recording audio into durable object storage so longer recordings are not limited by local note payload size.",
    ],
  },
  {
    date: "July 1, 2026",
    headline: "Storage plans, permissions, and editor polish",
    entries: [
      "Added per-plan cloud storage quotas for recording and media-heavy notes.",
      "Improved recording permission guidance and repair actions.",
      "Refined narrow-screen layouts, tag browsing, update checks, and support flows.",
      "Added visible build timing in settings so users can tell which app version they are running.",
    ],
  },
  {
    date: "June 28, 2026",
    headline: "Capture menu, Focus view, and mobile media fixes",
    entries: [
      "Consolidated note, camera, and recording capture into a draggable create menu.",
      "Added Focus view improvements for canvas text, photos, captions, and adding content back to the canvas.",
      "Improved iPad and mobile recording, camera switching, and live transcript behavior.",
      "Delivered export, wiki-link, folder drag-and-drop, and auto-summary improvements promised on the homepage.",
    ],
  },
  {
    date: "June 27, 2026",
    headline: "Expanded learning and comparison content",
    entries: [
      "Added a note-taking by profession hub and strengthened internal links across the public knowledge base.",
      "Improved template, comparison, and FAQ content for people evaluating different note-taking workflows.",
    ],
  },
  {
    date: "June 24, 2026",
    headline: "Free tools and on-device AI",
    entries: [
      "Expanded tool-first public pages and added on-device AI support to more free tools.",
      "Reworked growth content around higher-intent search topics while keeping product claims grounded.",
    ],
  },
  {
    date: "June 23, 2026",
    headline: "Free tools, privacy hardening, and marketing cleanup",
    entries: [
      "Launched the free tools section with practical note-taking, planning, and canvas utilities.",
      "Fixed accessibility, print, timer, link tracking, and state issues across the tools.",
      "Improved encrypted-content boundaries for AI features and fixed app deep links from tools.",
      "Added RSS, schema, internal linking, and conversion improvements to the marketing site.",
    ],
  },
  {
    date: "June 19, 2026",
    headline: "In-note camera capture and analytics",
    entries: [
      "Added a camera button to place photos directly onto the current note canvas.",
      "Published guidance for full-page web clipping and added product funnel tracking.",
    ],
  },
  {
    date: "June 17, 2026",
    headline: "Faster capture from desktop and PWA",
    entries: [
      "Added quick-capture shortcuts for new notes from the dock, deep links, and keyboard commands.",
      "Improved pasted prose handling so long text wraps cleanly in note content.",
    ],
  },
  {
    date: "June 8, 2026",
    headline: "Review content refresh",
    entries: [
      "Updated comparison pages with fresher structure, tables, FAQs, and discovery metadata.",
    ],
  },
  {
    date: "June 7, 2026",
    headline: "Mobile install guidance",
    entries: [
      "Replaced the iOS waitlist flow with clearer PWA installation steps for mobile users.",
    ],
  },
  {
    date: "June 6, 2026",
    headline: "Accessibility baseline",
    entries: [
      "Added an accessibility lint gate and fixed color contrast, icon labels, and related UI issues.",
    ],
  },
];

export function Changelog() {
  useMetaTags({
    title: "Changelog | OmniCanvas",
    description:
      "A daily product changelog for OmniCanvas, grouped from development history and written for users.",
    url: "https://omnicanvasnotes.com/changelog",
  });

  const latest = changelogDays[0];

  return (
    <div className="pt-28 pb-20">
      <div className="max-w-4xl mx-auto px-6">
        <div className="mb-12">
          <div className="inline-flex items-center gap-2 rounded-full bg-indigo-50 px-3 py-1 text-xs font-semibold uppercase tracking-[0.18em] text-indigo-700">
            <Sparkles size={14} />
            Product Updates
          </div>
          <h1 className="mt-5 text-4xl font-extrabold tracking-tight text-slate-900 sm:text-5xl">
            OmniCanvas Changelog
          </h1>
          <p className="mt-4 max-w-2xl text-lg leading-8 text-slate-600">
            A daily, public record of what is improving in OmniCanvas. These notes are summarized
            from development history and rewritten for customers instead of copying internal commit
            messages.
          </p>
          <p className="mt-3 text-sm text-slate-500">
            Last updated: {latest.date}
          </p>
        </div>

        <div className="space-y-6">
          {changelogDays.map((day) => (
            <section
              key={day.date}
              className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm"
            >
              <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
                <div>
                  <h2 className="text-xl font-bold text-slate-900">{day.headline}</h2>
                  <div className="mt-2 flex items-center gap-2 text-sm font-medium text-slate-500">
                    <CalendarDays size={16} className="text-indigo-500" />
                    <time dateTime={toIsoDate(day.date)}>{day.date}</time>
                  </div>
                </div>
              </div>

              <ul className="mt-5 space-y-3">
                {day.entries.map((entry) => (
                  <li key={entry} className="flex gap-3 text-sm leading-6 text-slate-700">
                    <CheckCircle2 size={16} className="mt-1 shrink-0 text-indigo-500" />
                    <span>{entry}</span>
                  </li>
                ))}
              </ul>
            </section>
          ))}
        </div>
      </div>
    </div>
  );
}

function toIsoDate(date: string): string {
  const parsed = new Date(`${date} 00:00:00 UTC`);
  return Number.isNaN(parsed.getTime()) ? date : parsed.toISOString().slice(0, 10);
}
